import React, { useEffect, useState } from 'react';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../config/appStore';
import { buildApiUrl } from '../../config/api';

type Store = 'ios' | 'android';

// Fire-and-forget: la analítica nunca debe afectar a la UX del banner.
const track = (event: 'banner_shown' | 'banner_click' | 'banner_dismiss', store: Store) => {
  try {
    const token = localStorage.getItem('token');
    fetch(buildApiUrl('/api/app-events'), {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ event, platform: 'web', metadata: { source: 'pwa_banner', store } }),
    }).catch(() => {});
  } catch {
    /* noop */
  }
};

const SNOOZE_KEY = 'appstore_banner_snooze_until';
const SNOOZE_DAYS = 30;
const SHOW_DELAY_MS = 2500;

const ANDROID_PACKAGE = 'com.elevatestudio.guideme';

// Abre la app si está instalada; si no, Chrome sigue el fallback hasta la ficha
// de Play. iOS no tiene equivalente sin Universal Links, así que va a la ficha.
const androidIntentUrl = () =>
  `intent://#Intent;scheme=guideme;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(
    PLAY_STORE_URL || `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`
  )};end`;

// iOS no tiene fallback automático como el intent de Android: se prueba el
// esquema de la app y, si en 1,2 s la página sigue visible (señal de que no se
// abrió nada), se cae a la ficha del App Store.
// Sin ruta: cualquier path que no exista en el router de la app abre su
// pantalla de "esta pantalla no existe". La raíz entra por la pantalla inicial.
const APP_SCHEME_URL = 'guideme://';
const SCHEME_TIMEOUT_MS = 1200;

const openIosApp = (storeUrl: string) => {
  const fallback = setTimeout(() => {
    if (!document.hidden) window.location.href = storeUrl;
  }, SCHEME_TIMEOUT_MS);
  const cancel = () => {
    if (document.hidden) clearTimeout(fallback);
  };
  document.addEventListener('visibilitychange', cancel, { once: true });
  window.location.href = APP_SCHEME_URL;
};

const STORE_COPY: Record<Store, { title: string; action: string; url: string }> = {
  ios: { title: 'GuideMe para iPhone', action: 'Abrir en la app', url: APP_STORE_URL },
  android: { title: 'GuideMe para Android', action: 'Abrir en la app', url: PLAY_STORE_URL },
};

const detectStore = (): Store | null => {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  return /Android/i.test(ua) ? 'android' : null;
};

// Ya instalada: PWA en standalone o abierta dentro de una TWA de Android.
const isInstalled = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as { standalone?: boolean }).standalone === true ||
  document.referrer.startsWith('android-app://');

const isSnoozed = (): boolean => {
  const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
  return Date.now() < until;
};

// No competir con los overlays de primera visita: esperar a que el aviso de
// cookies esté respondido y el carrusel de intro visto antes de aparecer.
const firstVisitOverlaysDone = (): boolean =>
  Boolean(localStorage.getItem('guideme-cookie-consent')) &&
  Boolean(localStorage.getItem('guideme_intro_seen'));

const AppDownloadBanner: React.FC = () => {
  const [store, setStore] = useState<Store | null>(null);

  useEffect(() => {
    const target = detectStore();
    if (!target || isInstalled() || isSnoozed()) return;
    let showTimer: ReturnType<typeof setTimeout> | undefined;
    const poll = setInterval(() => {
      if (!firstVisitOverlaysDone()) return;
      clearInterval(poll);
      showTimer = setTimeout(() => {
        setStore(target);
        track('banner_shown', target);
      }, SHOW_DELAY_MS);
    }, 1000);
    return () => {
      clearInterval(poll);
      if (showTimer) clearTimeout(showTimer);
    };
  }, []);

  if (!store) return null;

  const copy = STORE_COPY[store];

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
    setStore(null);
  };

  const dismiss = () => {
    track('banner_dismiss', store);
    snooze();
  };

  // Abrir la app instalada. Ni intent:// ni el esquema admiten target _blank:
  // tienen que navegar la propia pestaña.
  const openApp = () => {
    track('banner_click', store);
    snooze();
    if (store === 'android') {
      window.location.href = androidIntentUrl();
      return;
    }
    openIosApp(copy.url);
  };

  // Para quien todavía no la tiene: directo a la ficha, sin intentar abrir nada.
  const openStore = () => {
    track('banner_click', store);
    snooze();
    window.open(copy.url, '_blank', 'noopener');
  };

  return (
    <div className="lg:hidden fixed bottom-20 left-3 right-3 z-50 animate-in slide-in-from-bottom duration-500 safe-area-inset-bottom">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-3.5">
        <div className="flex items-center gap-3">
          <img
            src="/icon-192.png"
            alt="GuideMe"
            className="w-12 h-12 rounded-xl shadow-sm flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
              {copy.title}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
              Más rápida, con captura IA y avisos de tus servicios.
            </p>

          </div>
          <button
            onClick={dismiss}
            aria-label="Cerrar"
            className="self-start -mt-1 -mr-1 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={openApp}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-full transition-colors"
          >
            {copy.action}
          </button>
          <button
            onClick={openStore}
            className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 px-3 py-2 whitespace-nowrap"
          >
            No la tengo
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppDownloadBanner;
